import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingCart, Trash2, Plus, Minus, ArrowRight, ArrowLeft, ImageIcon, Calendar, Clock, Tag, Gift } from 'lucide-react';
import { useCart } from '@/lib/CartContext';
import { format, parseISO } from 'date-fns';

const EXPERIENCE_LABELS = {
  adult_sim_racing: 'Adult Sim Racing',
  child_sim_racing: 'Child Sim Racing',
  young_racer: 'Young Racer',
};

const RIG_LABELS = {
  sabelt_amg: 'Sabelt AMG',
  sabelt_porsche: 'Sabelt Porsche',
  treq_fanatec: 'TREQ / Fanatec',
  child_sim: 'Child Sim Rig',
};

export default function Cart() {
  const { items, totalItems, totalPrice, updateQuantity, removeItem, loading } = useCart();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#0d0d0f] pt-16">
      <section className="py-14 px-6 lg:px-10 border-b border-white/5">
        <div className="max-w-5xl mx-auto">
          <p className="text-primary text-xs font-bold tracking-widest mb-2">YOUR CART</p>
          <h1 className="text-white font-black text-3xl md:text-4xl uppercase">
            {totalItems > 0 ? `${totalItems} ITEM${totalItems !== 1 ? 'S' : ''}` : 'YOUR CART IS EMPTY'}
          </h1>
        </div>
      </section>

      <section className="px-6 lg:px-10 py-10 max-w-5xl mx-auto">
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-20 border border-white/5 bg-[#111115]">
            <ShoppingCart className="w-12 h-12 text-white/10 mx-auto mb-4" />
            <p className="text-white/40 text-sm mb-6">No items in your cart yet.</p>
            <div className="flex items-center gap-3 justify-center flex-wrap">
              <Link to="/shop">
                <button className="border border-white/10 hover:border-primary/50 text-white/50 hover:text-white font-bold text-sm px-6 py-3 transition-colors">
                  BROWSE SHOP
                </button>
              </Link>
              <Link to="/bookings">
                <button className="bg-primary hover:bg-primary/90 text-white font-bold text-sm px-8 py-3 transition-colors flex items-center gap-2">
                  BOOK A SESSION
                </button>
              </Link>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Items list */}
            <div className="lg:col-span-2 space-y-3">
              <AnimatePresence>
                {items.map((item) => (
                  <motion.div
                    key={item.cartItemId || item.product_id}
                    layout
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="flex gap-4 bg-[#111115] border border-white/5 p-4"
                  >
                    {item.type === 'booking' ? (
                      // Booking item
                      <>
                        <div className="w-20 h-20 bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
                          <Calendar className="w-7 h-7 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <span className="text-primary text-[10px] font-bold tracking-widest">SESSION BOOKING</span>
                          <p className="text-white font-bold text-sm mt-0.5">{EXPERIENCE_LABELS[item.experience_type] || item.experience_type}</p>
                          <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1.5">
                            {item.date && (
                              <span className="text-white/40 text-xs flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {format(parseISO(item.date), 'dd MMM yyyy')}
                              </span>
                            )}
                            {item.time_slot && (
                              <span className="text-white/40 text-xs flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {item.time_slot}
                              </span>
                            )}
                            {item.rig_type && (
                              <span className="text-white/40 text-xs flex items-center gap-1">
                                <Tag className="w-3 h-3" />
                                {RIG_LABELS[item.rig_type] || item.rig_type}
                              </span>
                            )}
                          </div>
                          <p className="text-primary font-black text-lg mt-1">£{item.price.toFixed(2)}</p>
                        </div>
                        <button
                          onClick={() => removeItem(item.cartItemId)}
                          className="text-white/20 hover:text-red-400 transition-colors self-start"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </>
                    ) : item.type === 'giftcard' ? (
                      // Gift card item
                      <>
                        <div className="w-20 h-20 bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
                          <Gift className="w-7 h-7 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <span className="text-primary text-[10px] font-bold tracking-widest">GIFT CARD</span>
                          <p className="text-white font-bold text-sm mt-0.5">{item.product_name}</p>
                          <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1.5">
                            <span className="text-white/40 text-xs flex items-center gap-1">
                              <Gift className="w-3 h-3" />
                              {item.delivery_method === 'post' ? 'Postal delivery' : 'Email delivery'}
                            </span>
                            {item.send_to_self
                              ? <span className="text-white/40 text-xs">For: yourself</span>
                              : item.recipient_name && <span className="text-white/40 text-xs">For: {item.recipient_name}</span>
                            }
                          </div>
                          <p className="text-primary font-black text-lg mt-1">£{item.price.toFixed(2)}</p>
                        </div>
                        <button
                          onClick={() => removeItem(item.cartItemId)}
                          className="text-white/20 hover:text-red-400 transition-colors self-start"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </>
                    ) : (
                      // Product item
                      <>
                        <div className="w-20 h-20 bg-[#0d0d0f] border border-white/5 flex items-center justify-center shrink-0 overflow-hidden">
                          {item.product_image_url ? (
                            <img src={item.product_image_url} alt={item.product_name} className="w-full h-full object-cover" />
                          ) : (
                            <ImageIcon className="w-5 h-5 text-white/20" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-bold text-sm truncate">{item.product_name}</p>
                          <p className="text-primary font-black text-lg mt-0.5">£{(item.price * item.quantity).toFixed(2)}</p>
                          {item.quantity > 1 && (
                            <p className="text-white/30 text-xs">£{item.price.toFixed(2)} each</p>
                          )}
                        </div>
                        <div className="flex flex-col items-end justify-between gap-2 shrink-0">
                          <button
                            onClick={() => removeItem(item.product_id)}
                            className="text-white/20 hover:text-red-400 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                          <div className="flex items-center gap-2 border border-white/10">
                            <button
                              onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                              className="w-8 h-8 flex items-center justify-center text-white/50 hover:text-white hover:bg-white/5 transition-all"
                            >
                              <Minus className="w-3 h-3" />
                            </button>
                            <span className="text-white text-sm font-bold w-6 text-center">{item.quantity}</span>
                            <button
                              onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                              className="w-8 h-8 flex items-center justify-center text-white/50 hover:text-white hover:bg-white/5 transition-all"
                            >
                              <Plus className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                  </motion.div>
                ))}
              </AnimatePresence>

              <div className="flex gap-4 mt-4 flex-wrap">
                <Link to="/shop" className="inline-flex items-center gap-2 text-white/30 hover:text-white text-xs font-bold tracking-widest transition-colors">
                  <ArrowLeft className="w-3.5 h-3.5" /> CONTINUE SHOPPING
                </Link>
                <Link to="/bookings" className="inline-flex items-center gap-2 text-white/30 hover:text-white text-xs font-bold tracking-widest transition-colors">
                  + ADD ANOTHER SESSION
                </Link>
              </div>
            </div>

            {/* Order summary */}
            <div className="lg:col-span-1">
              <div className="bg-[#111115] border border-white/5 p-6 sticky top-24">
                <p className="text-primary text-[10px] font-bold tracking-widest mb-5">ORDER SUMMARY</p>
                <div className="space-y-0">
                  {items.map(item => (
                    <div key={item.cartItemId || item.product_id} className="flex justify-between py-2.5 border-b border-white/5 text-sm">
                      <span className="text-white/50 truncate pr-2">
                        {item.type === 'booking'
                          ? `${EXPERIENCE_LABELS[item.experience_type] || 'Session'} ×1`
                          : item.type === 'giftcard'
                            ? `${item.product_name} ×1`
                            : `${item.product_name} ×${item.quantity}`}
                      </span>
                      <span className="text-white shrink-0">£{(item.price * item.quantity).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between items-center pt-5 mt-2">
                  <span className="text-white font-bold text-sm">TOTAL</span>
                  <span className="text-primary font-black text-2xl">£{totalPrice.toFixed(2)}</span>
                </div>
                <p className="text-white/20 text-xs mt-2 mb-5">
                  {items.some(i => i.type === 'product') ? 'Delivery calculated at checkout' : 'No delivery required for session bookings'}
                </p>
                <button
                  onClick={() => navigate('/checkout')}
                  className="w-full bg-primary hover:bg-primary/90 text-white font-bold text-sm py-4 transition-colors flex items-center justify-center gap-2"
                >
                  CHECKOUT <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}